import { useRef, useMemo, useEffect, useState } from 'react';
import isDate from 'lodash/isDate';
import firebase from 'firebase';
import isArray from 'lodash/isArray';
import { Collections } from '../../../../../../firebase/firestore/collections';
import { useQuerySnapshot } from '../../../../../../firebase/firestore/hooks/useQuerySnapshot';
import { Query } from '../../../../../../firebase/firestore/query/query';
import { Criteria } from '../../../../../../firebase/firestore/query/criteria';
import { AppointmentStatus } from '../../../../../../firebase/firestore/documents/appointmentStatus';
import { Candidate, RecruiterBasedAppointment } from '../../../../../../firebase/firestore/documents/candidate';
import { formatDocument } from '../../../../../../firebase/firestore/documentConverter';
import { AppointmentAvailability } from '../../../../../../firebase/firestore/documents/appointmentAvaiability';

export interface NextUpcomingAppointment {
  appointment: RecruiterBasedAppointment;
  candidateId: string;
  candidate: Candidate;
  recruiterId: string | null;
}

interface UseNextUpcomingBookingParams {
  seekerId: string | null | undefined;
  accountId: string | null | undefined;
  userId?: string;
}

const GRACE_PERIOD_MS = 5 * 60 * 1000;

const criteriaCache = new Map<string, Criteria[]>();

function getCandidateCriteria(seekerId: string, accountId: string): Criteria[] {
  const key = `${seekerId}:${accountId}`;
  if (!criteriaCache.has(key)) {
    criteriaCache.clear();
    criteriaCache.set(key, [
      Query.field<Candidate>('seeker').equals(seekerId),
      Query.field<Candidate>('account').equals(accountId),
      Query.field<Candidate>('monetized').equals(true),
    ]);
  }
  return criteriaCache.get(key)!;
}

function toDate(value: unknown): Date {
  if (isDate(value)) return value;
  return new Date(value as string);
}

function toMs(value: unknown): number {
  return toDate(value).getTime();
}

function isAppointmentActive(appt: RecruiterBasedAppointment, now: number): boolean {
  if (appt.status !== AppointmentStatus.accepted) return false;
  if (!appt.fromDate) return false;

  const startMs = toMs(appt.fromDate);
  const endMs = appt.toDate ? toMs(appt.toDate) + GRACE_PERIOD_MS : startMs + GRACE_PERIOD_MS;

  return now < endMs;
}

function resolveNextUpcomingAppointment(candidates: Candidate[]): Omit<NextUpcomingAppointment, 'recruiterId'> | null {
  const now = Date.now();
  let next: Omit<NextUpcomingAppointment, 'recruiterId'> | null = null;
  let nextMs = Infinity;

  // eslint-disable-next-line no-restricted-syntax
  for (const candidate of candidates) {
    const { appointment, id: candidateId } = candidate;
    // eslint-disable-next-line no-continue
    if (!appointment) continue;

    const appt = appointment as RecruiterBasedAppointment;
    // eslint-disable-next-line no-continue
    if (!isAppointmentActive(appt, now)) continue;

    const fromMs = toMs(appt.fromDate!);

    /* istanbul ignore else */
    if (fromMs < nextMs) {
      nextMs = fromMs;
      next = {
        appointment: {
          ...appt,
          fromDate: new Date(fromMs),
          toDate: appt.toDate ? toDate(appt.toDate) : undefined,
        },
        candidateId,
        candidate,
      };
    }
  }

  return next;
}

type Fingerprint = [candidateId: string, fromMs: number] | null | undefined;

function getFingerprint(result: Omit<NextUpcomingAppointment, 'recruiterId'> | null | undefined): Fingerprint {
  if (result === undefined) return undefined;
  if (result === null) return null;
  return [result.candidateId, toMs(result.appointment.fromDate!)];
}

function fingerprintsEqual(a: Fingerprint, b: Fingerprint): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return a[0] === b[0] && a[1] === b[1];
}

export function useNextUpcomingBooking({
  seekerId,
  accountId,
  userId,
}: UseNextUpcomingBookingParams): NextUpcomingAppointment | null | undefined {
  const criteria = seekerId && accountId ? getCandidateCriteria(seekerId, accountId) : null;

  const candidates = useQuerySnapshot<Candidate>(Collections.CANDIDATES, criteria);

  const stableRef = useRef<{
    fingerprint: Fingerprint;
    result: Omit<NextUpcomingAppointment, 'recruiterId'> | null | undefined;
  }>({ fingerprint: undefined, result: undefined });

  const resolved = useMemo(() => {
    if (candidates === undefined) return undefined;
    if (!seekerId || !accountId) return null;
    if (candidates.length === 0) return null;

    const filtered = userId
      ? candidates.filter(
          (c) =>
            (isArray(c?.assignedRecruiters) && c.assignedRecruiters.includes(userId)) ||
            (isArray(c?.watchedRecruiters) && c.watchedRecruiters.includes(userId)),
        )
      : candidates;

    return resolveNextUpcomingAppointment(filtered);
  }, [candidates, seekerId, accountId, userId]);

  const nextFp = getFingerprint(resolved);

  if (!fingerprintsEqual(stableRef.current.fingerprint, nextFp)) {
    stableRef.current = { fingerprint: nextFp, result: resolved };
  }

  const stableResolved = stableRef.current.result;

  // Extract the IDs needed to fetch availability — only re-fetch when they actually change
  // eslint-disable-next-line
  const availabilityId = (stableResolved as any)?.appointment?.appointmentAvailabilityId as string | undefined;
  // eslint-disable-next-line
  const bookingId = (stableResolved as any)?.appointment?.bookingId as string | undefined;

  const fetchKeyRef = useRef<string | null>(null);
  const [recruiterId, setRecruiterId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    // Still loading candidates
    if (stableResolved === undefined) return;

    // No active appointment — resolve to null immediately
    if (stableResolved === null || !availabilityId || !bookingId) {
      setRecruiterId(null);
      return;
    }

    const fetchKey = `${availabilityId}:${bookingId}`;

    /* istanbul ignore next */
    if (fetchKeyRef.current === fetchKey) return;
    fetchKeyRef.current = fetchKey;

    let cancelled = false;

    firebase
      .firestore()
      .collection(Collections.APPOINTMENT_AVAILABILITY)
      .doc(availabilityId)
      .get()
      .then((snap) => {
        if (cancelled) return;
        const availability = snap.exists
          ? formatDocument<AppointmentAvailability>(snap) ?? /* istanbul ignore next */ null
          : null;
        setRecruiterId(availability?.userId ?? null);
      })
      .catch(() => {
        /* istanbul ignore next */
        if (!cancelled) setRecruiterId(null);
      });

    // eslint-disable-next-line
    return () => {
      cancelled = true;
    };
  }, [stableResolved, availabilityId, bookingId]);

  // Still waiting on candidates snapshot
  if (stableResolved === undefined) return undefined;

  // No active appointment
  if (stableResolved === null) return null;

  // Appointment found but availability fetch not yet settled
  if (recruiterId === undefined) return undefined;

  return {
    ...stableResolved,
    recruiterId,
  };
}
